import CardVoucher from '@/components/sections/builder/elements/body/cart-total/component/card-voucher';
import VoucherList from '@/components/sections/builder/elements/body/checkout-form/component/voucher-list';
import { CheckoutFormElemetDataType } from '@/components/sections/builder/elements/body/checkout-form/type';
import { ScrollArea } from '@/components/ui/scroll-area';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  calculateTotalDiscountPerPriority,
  cn,
  formatIDR,
  hexToRgba,
  isEmpty,
  truncateText,
} from '@/lib/utils';
import { ThemeColorSchemeType, ThemePromotionType } from '@/stores/builder';
import { HttpTypes } from '@/types';
import { BaseCart } from '@/types/http/cart/common';
import {
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  ShoppingBagIcon,
  TicketPercent,
} from 'lucide-react';
import Image from 'next/image';
import React, { useMemo } from 'react';

type CheckoutItemProps = {
  content: CheckoutFormElemetDataType;
  carts: HttpTypes.StoreCartDetail;
  isShippingInsurance: boolean;
  isLoadingSelectShipping: boolean;
  className?: string;
  promotionTheme?: ThemePromotionType;
};

export const CheckoutItem: React.FC<CheckoutItemProps> = ({
  content,
  carts,
  isShippingInsurance,
  isLoadingSelectShipping,
  className,
  promotionTheme,
}) => {
  const colorScheme = content.colorSchemeItem as ThemeColorSchemeType;

  const [sectionView, setSectionView] = React.useState<'detail' | 'voucher' | 'active_offers'>(
    'detail'
  );

  const subtotal = carts?.subtotal as number;
  const discountAmount = carts?.discount_amount as number;
  const grandTotal = carts?.grand_total as number;
  const shippingInsurance = carts?.shipping_insurance as number;
  const CODFee = carts?.cod_fee as number;
  const discountPromotion = carts?.total_promotion_discount as number;

  const totalCOD = CODFee;

  const shipperPromotionRates = carts?.shipper_discount_rates as number;
  const total = isShippingInsurance
    ? grandTotal + shippingInsurance - shipperPromotionRates
    : grandTotal - shipperPromotionRates;

  const listPromotions = carts?.promotions;
  const hasSelectedPromos = (listPromotions ?? []).length > 0;
  const selectedPromosName = listPromotions?.map((promo) => promo.promotion_name).join(', ');

  const subtotalNonPromo = useMemo(() => {
    return carts?.items
      ?.filter((item) => !item.promotion_id)
      .reduce((sum, item) => sum + Number(item.subtotal), 0);
  }, [carts?.items]);

  const { updatedPromos } = calculateTotalDiscountPerPriority(
    (listPromotions ?? []) as HttpTypes.StoreCartDetailPromotion[],
    subtotalNonPromo as number
  );

  return (
    <>
      <div className={cn('bg_checkout_item flex flex-col p-6 md:w-[45%] md:p-9', className)}>
        <div className={cn(content.stickyContent && `top-sticky md:sticky`)}>
          {sectionView === 'detail' ? (
            <>
              <div className='flex items-center gap-x-2 pb-6'>
                <ShoppingBagIcon />
                <h2 className='text-xl'>Your Order Details</h2>
              </div>
              <h3 className='mb-4 text-base font-semibold'>Product</h3>

              {carts?.items
                ?.slice()
                .sort((a, b) => {
                  const aIsFree = !!a.promotion_id;
                  const bIsFree = !!b.promotion_id;
                  if (aIsFree === bIsFree) return 0;
                  return aIsFree ? 1 : -1;
                })
                .map((item: BaseCart, index: number) => {
                  const isDiscount = (item.discount_price ?? 0) > 0;

                  const isPricebook = item.pricebook_id !== null;

                  const hasPromoDiscount = item?.promotion_id;

                  const promoPrice =
                    Number(item?.sell_price) * item?.quantity - Number(item?.discount_amount);

                  return (
                    <div
                      className='mb-5 flex flex-col justify-between border-b pb-5 md:flex-row md:items-center md:gap-6'
                      key={index}
                    >
                      <div className='flex w-full max-w-full items-center gap-x-4 overflow-hidden'>
                        <div className='relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm'>
                          <Image
                            src={item.product_assets[0]?.assets_url}
                            alt={item.product_name}
                            fill
                            className='rounded-sm border border-gray-300 object-cover'
                          />
                          <span
                            style={
                              !isEmpty(content.colorSchemeForm)
                                ? {
                                    backgroundColor: content.colorSchemeForm?.btnSolidBackgroud,
                                    color: content?.colorSchemeForm?.btnSolidlabel,
                                  }
                                : {}
                            }
                            className='absolute right-0 top-0 flex h-5 w-5 items-center justify-center rounded-bl-sm bg-black/60 text-xs font-bold text-white'
                          >
                            {item.quantity}
                          </span>
                        </div>
                        <div className='flex w-full max-w-full flex-col overflow-hidden'>
                          <p className='truncate break-all text-sm md:break-words'>
                            {item.product_name}
                          </p>
                          <span className='truncate break-all text-xs text-gray-500 md:break-words'>
                            {item.sku}
                          </span>
                        </div>
                      </div>
                      <div className='flex w-full flex-col gap-2 text-right'>
                        {hasPromoDiscount ? (
                          <span className='ml-20 whitespace-nowrap text-sm font-bold md:ml-0'>
                            {hasPromoDiscount ? 'Free' : formatIDR(promoPrice)}
                          </span>
                        ) : (
                          isDiscount && (
                            <span className='ml-20 whitespace-nowrap text-sm font-bold md:ml-0'>
                              {formatIDR(
                                isPricebook
                                  ? (item?.sell_price as number) * item.quantity
                                  : (item?.discount_price as number) * item.quantity
                              )}
                            </span>
                          )
                        )}
                        <span
                          className={cn(
                            'ml-20 whitespace-nowrap text-sm md:ml-0',
                            item?.discount_price || hasPromoDiscount
                              ? 'text-[#C2C2C2] line-through'
                              : 'font-bold'
                          )}
                        >
                          {formatIDR(item?.sell_price * item.quantity)}
                        </span>
                      </div>
                    </div>
                  );
                })}

              {/* Promotion */}
              <div
                className='mt-1 flex w-full cursor-pointer items-center justify-between gap-5 border-b border-t border-gray-200 bg-[--background] px-3 py-4 text-sm font-bold text-[--text-primary]'
                style={
                  {
                    '--text-primary': promotionTheme?.colorScheme?.text || '#52535B',
                    '--text-secondary': promotionTheme?.colorScheme?.textSecondary || '#52535B',
                    '--background':
                      hexToRgba(promotionTheme?.colorScheme?.background, 0.1) || 'white',
                  } as React.CSSProperties
                }
              >
                <button
                  className='flex items-center gap-2'
                  style={{
                    outline: 'none',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSectionView('voucher')}
                >
                  <TicketPercent className='size-6' />
                  <span className='line-clamp-1'>
                    {hasSelectedPromos
                      ? truncateText(selectedPromosName as string, 35)
                      : 'See all offers'}
                  </span>
                </button>
                <button
                  type='button'
                  onClick={() => setSectionView('active_offers')}
                  className='flex items-center gap-2'
                  style={{
                    outline: 'none',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                  aria-label='Show active offers'
                >
                  <ChevronRight className='size-5' />
                </button>
              </div>

              {/* Summary total */}
              <div className='mt-6 flex w-full items-center justify-between border-b pb-4 text-sm'>
                <span>Subtotal</span>
                <span>{formatIDR(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className='mt-6 flex w-full items-center justify-between border-b pb-4 text-sm'>
                  <span>Product discounts</span>
                  <span className='text-red-500'>{formatIDR(discountAmount)}</span>
                </div>
              )}
              {discountPromotion > 0 && (
                <div className='mt-6 flex w-full items-center justify-between border-b pb-4 text-sm'>
                  <span>Transaction discounts</span>
                  <span className='text-red-500'>{formatIDR(discountPromotion)}</span>
                </div>
              )}
              {isLoadingSelectShipping ? (
                <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
                  <span className=''>Shipping</span>
                  <span>Estimated...</span>
                </div>
              ) : (
                carts?.shipping && (
                  <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
                    <span>Shipping</span>
                    <span className='flex flex-col items-end'>
                      {carts?.shipper_discount_rates ? (
                        <>
                          <span className='text-muted-foreground line-through'>
                            {formatIDR(carts.shipping_cost || 0)}
                          </span>
                          <span
                            className={cn(
                              'font-semibold',
                              (carts.shipping_cost ?? 0) - carts.shipper_discount_rates === 0
                                ? 'text-red-500'
                                : 'text-muted-foreground'
                            )}
                          >
                            {(carts.shipping_cost ?? 0) - carts.shipper_discount_rates === 0
                              ? 'FREE'
                              : formatIDR(
                                  (carts.shipping_cost ?? 0) - carts.shipper_discount_rates
                                )}
                          </span>
                        </>
                      ) : (
                        <span>{formatIDR(carts.shipping_cost || 0)}</span>
                      )}
                    </span>
                  </div>
                )
              )}
              {isShippingInsurance && (
                <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
                  <span className=''>Shipping Insurance</span>
                  <span>{formatIDR(Number(shippingInsurance))}</span>
                </div>
              )}
              {carts?.is_cod && (
                <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
                  <span className=''>COD fee</span>
                  <span>{formatIDR(Number(totalCOD))}</span>
                </div>
              )}
              {carts?.charge_fee_to === 'buyer' && (
                <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
                  <span className=''>Platform fee</span>
                  <span>{formatIDR(Number(carts?.platform_fee))}</span>
                </div>
              )}

              <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
                <div className='flex items-center gap-x-2'>
                  <span>Tax</span>
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger>
                        <CircleHelp className='h-4 w-4 text-gray-600' />
                      </TooltipTrigger>
                      <TooltipContent className='w-40 bg-black/80 text-center text-white'>
                        <p>
                          The final tax and total will be confirmed by email or text after you place
                          your order.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <span>{formatIDR(carts?.tax_amount as number)}</span>
              </div>
              <div className='checkout_item_content mt-6 flex items-center justify-between text-xl'>
                <span>Total</span>
                <span>{formatIDR(total)}</span>
              </div>
            </>
          ) : sectionView === 'active_offers' ? (
            <>
              <div className='flex items-center gap-x-2 pb-6'>
                <ChevronLeft className='cursor-pointer' onClick={() => setSectionView('detail')} />
                <h2 className='text-xl'>All offers active</h2>
              </div>

              <ScrollArea className='max-h-[600px] overflow-y-auto pt-4 lg:px-4' type='scroll'>
                {updatedPromos && updatedPromos.length > 0 ? (
                  updatedPromos?.map((promo, index) => (
                    <CardVoucher
                      key={index}
                      theme={promotionTheme}
                      themeCart={colorScheme}
                      promo={promo}
                      minSpending={promo.minimum_spending}
                      grandTotal={grandTotal}
                      isShowReward
                      isCartDrawer
                    />
                  ))
                ) : (
                  <p className='text-center text-sm'>No offers active</p>
                )}
              </ScrollArea>
            </>
          ) : (
            <VoucherList
              carts={carts}
              sectionView={sectionView}
              setSectionView={setSectionView}
              colorScheme={colorScheme}
              promotionTheme={promotionTheme}
            />
          )}
        </div>
      </div>
      <style jsx>
        {`
          .bg_checkout_item {
            background-color: ${content.colorSchemeItem?.background || 'white'};
            color: ${content.colorSchemeItem?.text || '#52535B'};
            border-radius: ${content.radiusCheckoutItem || 0}px;
          }
          @media (min-width: 768px) {
            .top-sticky {
              top: ${content.topContent || 0}px;
            }
          }
          .checkout_item_content {
            color: ${content.colorSchemeItem?.textSecondary || '#52535B'};
            font-weight: 700;
          }
        `}
      </style>
    </>
  );
};
